/**
 * htmlJsonMergeUtil_Constants.ts - Type prefix constants for HtmlJsonMergeUtil
 * Pure ES module - works in both Node.js and browser via bundler
 *
 * Uses new HtmlJson format with type prefixes:
 * s=Html, k=JsonKey, K=JsonKeySkip, p=Placeholder, P=PlaceholderSkip,
 * h=Slotholder, H=SlotholderSkip, a=ArrayBlock, A=ArrayBlockSkip, x=SlotHtml, i=Slot, c=Conditional
 */

import { JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/index.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

// Type prefix constants for new format
export const TYPE_HTML = 's';
export const TYPE_JSONKEY = 'k';
export const TYPE_JSONKEY_SKIP = 'K';
export const TYPE_PLACEHOLDER = 'p';
export const TYPE_PLACEHOLDER_SKIP = 'P';
export const TYPE_SLOTHOLDER = 'h';
export const TYPE_SLOTHOLDER_SKIP = 'H';
export const TYPE_ARRAYBLOCK = 'a';
export const TYPE_ARRAYBLOCK_SKIP = 'A';
export const TYPE_SLOTHTML = 'x';
export const TYPE_SLOT = 'i';
export const TYPE_CONDITIONAL = 'c';
export const META_KEY = '_meta';

type JsonMapOrObject = PlainJsonMap | PlainJsonObject;

/**
 * Finds the actual key in an object using case-insensitive matching.
 * Returns the original-cased key if found, or null if not found.
 */
function findKeyCaseInsensitive(obj: JsonMapOrObject | null | undefined, key: string): string | null {
    if (!obj) return null;
    const keyLower = key.toLowerCase();
    if (obj instanceof Map) {
        for (const k of obj.keys()) {
            if (k.toLowerCase() === keyLower) return k;
        }
    } else {
        for (const k of Object.keys(obj)) {
            if (k.toLowerCase() === keyLower) return k;
        }
    }
    return null;
}

/**
 * Checks if an object (Map, JsonObject, or plain object) has a key.
 * Uses case-insensitive fallback to match Normal engine behavior.
 */
export function hasKey(obj: JsonMapOrObject | null | undefined, key: string): boolean {
    if (obj instanceof Map) {
        if (obj.has(key)) return true;
    } else if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
        return true;
    }
    // Case-insensitive fallback
    return findKeyCaseInsensitive(obj, key) !== null;
}

// [Dafny Verified] Properties V1-V3 — Verification/dafny/src/TryGetValueCaseInsensitive.dfy
/**
 * Gets a value from an object (Map, JsonObject, or plain object)
 * If the value is a JsonValue, returns the raw value.
 * Uses case-insensitive fallback to match Normal engine behavior.
 */
export function getValue(obj: JsonMapOrObject | null | undefined, key: string): unknown {
    let value: unknown;
    if (obj instanceof Map) {
        // Use has() before get() because JsonObject.get() returns JsonValue.null()
        // instead of undefined for missing keys, which would prevent case-insensitive fallback
        if (obj.has(key)) {
            value = obj.get(key);
        } else {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = obj.get(actualKey);
        }
    } else {
        value = obj ? (obj as PlainJsonObject)[key] : undefined;
        if (value === undefined && obj) {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = (obj as PlainJsonObject)[actualKey];
        }
    }

    // Unwrap JsonValue to get raw value
    if (value instanceof JsonValue) {
        return unwrapJsonValue(value);
    }
    return value;
}

/**
 * Gets the JsonValue from an object (Map, JsonObject, or plain object)
 * Uses case-insensitive fallback to match Normal engine behavior.
 */
export function getJsonValue(obj: JsonMapOrObject | null | undefined, key: string): JsonValue {
    if (obj instanceof Map) {
        let value = obj.has(key) ? obj.get(key) : undefined;
        if (value === undefined) {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = obj.get(actualKey);
        }
        return value !== undefined ? value as JsonValue : JsonValue.null();
    }
    let value = obj ? (obj as PlainJsonObject)[key] : undefined;
    if (value === undefined && obj) {
        const actualKey = findKeyCaseInsensitive(obj, key);
        if (actualKey !== null) value = (obj as PlainJsonObject)[actualKey];
    }
    if (value === undefined) {
        return JsonValue.null();
    }
    if (value instanceof JsonValue) {
        return value;
    }
    // Wrap raw value in JsonValue
    return JsonValue.from(value);
}

/**
 * Unwrap a JsonValue to get the raw value
 */
function unwrapJsonValue(jsonValue: JsonValue): unknown {
    switch (jsonValue.typeName) {
        case JsonValueType.String:
            return jsonValue.asString();
        case JsonValueType.Number:
            return jsonValue.asNumber();
        case JsonValueType.Integer:
            return jsonValue.asInteger();
        case JsonValueType.Bool:
            return jsonValue.asBool();
        case JsonValueType.Array:
            return jsonValue.asArray();
        case JsonValueType.Object:
            return jsonValue.asObject();
        case JsonValueType.Null:
            return null;
        default:
            return null;
    }
}

/**
 * Extracts the name from a key (e.g., "k2_Title" -> "Title")
 */
export function getKeyName(key: string): string {
    const underscoreIdx = key.indexOf('_');
    return underscoreIdx >= 0 ? key.substring(underscoreIdx + 1) : '';
}

/**
 * Checks if this is a type-prefixed key (new format: type char + digit, e.g., "k2_Title", "s1")
 * vs a regular key like "PLACEHOLDER", "MainBlock", etc.
 */
export function isTypePrefixedKey(key: string): boolean {
    return key.length >= 2 && /\d/.test(key[1]);
}
